import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import React from 'react';

interface ContentSection {
    title: string;
    paragraphs?: string[];
    bullets?: string[];
    ordered_list?: string[];
    bold_bullets?: string[];
    link?: {
        text: string;
        url: string;
        style?: 'primary' | 'dark';
    };
}

interface PageConfig {
    title: string;
    video_url?: string;
    intro?: string;
    info_link?: {
        text: string;
        label: string;
        url: string;
    };
    info_links?: {
        text: string;
        label: string;
        url: string;
    }[];
    sections: ContentSection[];
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
        config: PageConfig;
    };
}

const ConquerEliteContent: React.FC = () => {
    const { static_page } = usePage<{ static_page: PageProps['static_page'] }>()
        .props;
    const config = static_page.config;

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="mx-auto max-w-5xl space-y-6">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
                    {config.title}
                </h1>

                {config.video_url && (
                    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm">
                        <div
                            className="relative"
                            style={{ paddingTop: '56.25%' }}
                        >
                            <iframe
                                src={config.video_url}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full"
                                style={{ border: 0 }}
                                allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                allowFullScreen={true}
                                title={config.title}
                            />
                        </div>
                    </div>
                )}

                {config.intro && (
                    <p className="text-lg text-gray-700 dark:text-neutral-300">{config.intro}</p>
                )}

                {config.info_link && (
                    <div>
                        <p className="mb-2 text-lg text-gray-700 dark:text-neutral-300">
                            {config.info_link.label}
                        </p>
                        <a
                            href={config.info_link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="primary-button inline-flex items-center"
                        >
                            {config.info_link.text}
                        </a>
                    </div>
                )}

                {config.info_links && (
                    <div className="space-y-4">
                        {config.info_links.map((link, i) => (
                            <div key={i}>
                                <p className="mb-2 text-lg text-gray-700 dark:text-neutral-300">
                                    {link.label}
                                </p>
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="primary-button inline-flex items-center"
                                >
                                    {link.text}
                                </a>
                            </div>
                        ))}
                    </div>
                )}

                {config.sections.map((section, i) => (
                    <div key={i} className="space-y-3">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                            {section.title}
                        </h2>

                        {section.paragraphs?.map((p, j) => (
                            <p key={j} className="text-lg text-gray-700 dark:text-neutral-300">
                                {p}
                            </p>
                        ))}

                        {section.bold_bullets && (
                            <ul className="list-disc space-y-1 pl-6">
                                {section.bold_bullets.map((item, j) => (
                                    <li
                                        key={j}
                                        className="text-lg font-semibold text-gray-900 dark:text-white"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {section.bullets && (
                            <ul className="list-disc space-y-1 pl-6">
                                {section.bullets.map((item, j) => (
                                    <li
                                        key={j}
                                        className="text-lg text-gray-700 dark:text-neutral-300"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {section.ordered_list && (
                            <ol className="list-decimal space-y-1 pl-6">
                                {section.ordered_list.map((item, j) => (
                                    <li
                                        key={j}
                                        className="text-lg text-gray-700 dark:text-neutral-300"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ol>
                        )}

                        {section.link && (
                            <a
                                href={section.link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={
                                    section.link.style === 'dark'
                                        ? 'inline-flex items-center rounded-lg bg-gray-900 px-6 py-3 text-base font-semibold text-white transition hover:bg-gray-800'
                                        : 'primary-button inline-flex items-center'
                                }
                            >
                                {section.link.text}
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </AppLayout>
    );
};

const ConquerElite: React.FC = () => (
    <ThemeWrapper>
        <ConquerEliteContent />
    </ThemeWrapper>
);

export default ConquerElite;
